import React, { Dispatch, ReactNode, SetStateAction } from "react";

export type ColumnId = string;
export type DataType =
  | "string"
  | "number"
  | "bool"
  | "date_iso"
  | "date_unix_ms"
  | "array"
  | "component"
  | (string & {});

export type CellComponentProps<T> = {
  value: any;
  columnSchema: ColumnSchema;
  isDisabled: boolean;
} & T;

export type SortDirection = "asc" | "desc";
export function isSortDirection(value: string | null): value is SortDirection {
  return value === "asc" || value === "desc";
}

export interface CellComponent<T> extends React.FC<CellComponentProps<T>> {
  overrideEmptyCell?: boolean;
}

type Filters = Record<
  ColumnId,
  {
    fieldId: string;
    data: any[];
  }
>;
type SortData = Record<
  ColumnId,
  {
    fieldId: string;
    direction: SortDirection;
  }
>;
export type FilterControl = {
  filters: Filters;
  sortData: SortData;
  setFilters: Dispatch<SetStateAction<Filters>>;
  setSortData: Dispatch<SetStateAction<SortData>>;
  applyFilter: (
    columnId: ColumnId,
    data: { filterData?: any[]; sortDir: string | null },
  ) => void;
};

export type FilterComponentProps<T> = {
  columnSchema: ColumnSchema;
  filterControl: FilterControl;
  onClose: () => void;
} & T;
export type FilterComponent<T> = ((
  props: FilterComponentProps<T>,
) => ReactNode) & {
  dropdownWidth?: number;
};

export type ColumnWidth = "sm" | "md" | "lg" | number;
export type ColumnWidthMap = Record<ColumnId, ColumnWidth>;

export type BaseColumnSchema = Record<string, any> & {
  header?: {
    component?: React.ComponentType<any>;
    props?: Record<string, any>;
  };
  dataType: DataType;
  fieldId?: string;
  width?: ColumnWidth;
  sticky?: boolean | "left" | "right";
  resizable?: boolean;
  showVerticalBorder?: boolean;
};

// The initial schema that the consumer provides to useSchema. This contains more optional fields which will be filled in by the formatter.
export type RawColumnSchema = BaseColumnSchema & {
  cell?: {
    component?: CellComponent<any>;
    props?: Record<string, any>;
    formatValue?: (value: any) => any;
  };
  filter?:
    | (Record<string, any> & {
        component?: FilterComponent<any>;
        fieldId?: string;
        props?: any;
      })
    | boolean;
  sort?:
    | (Record<string, any> & {
        fieldId?: string;
        ascLabel: string;
        descLabel: string;
      })
    | boolean;
};

// The formatted schema which is more stringent about what is required which makes it easier to work with in the table component.
export type ColumnSchema = BaseColumnSchema & {
  id: ColumnId;
  cell: {
    component: CellComponent<any>;
    props?: Record<string, any>;
    formatValue?: (value: any) => any;
  };
  filter:
    | (Record<string, any> & {
        component: FilterComponent<any>;
        fieldId: string;
        props?: any;
      })
    | false;
  sort:
    | (Record<string, any> & {
        fieldId: string;
      })
    | false;
};

export type RawTableSchema = Record<ColumnId, RawColumnSchema>;
export type TableSchema = Record<ColumnId, ColumnSchema>;

export type ResizeInfo = {
  columnId: ColumnId;
  width: number;
};

export type Resource = Record<string, any> & {
  id: string | number;
};
